import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { subHours, format } from 'date-fns';
import Header from '@/components/Header';
import { SidebarProvider } from '@/contexts/SidebarContext';
import AppSidebar from '@/components/AppSidebar';
import DashboardFooter from '@/components/DashboardFooter';
import { useSidebarContext } from '@/contexts/SidebarContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { Send, Loader2, Calendar, Hash, ArrowUpDown, Eye, EyeOff, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';

const RedditHNLayout: React.FC = () => {
  const { compact } = useSidebarContext();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    subReddit: '',
    timeUtc: '12' // Default 12 hours
  });
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<{seen: any[], unseen: any[]} | null>(null);
  const [activeTab, setActiveTab] = useState<'unpublished' | 'published'>('unpublished');
  const [sortBy, setSortBy] = useState<'date' | 'upvotes' | 'title' | 'ratio'>('upvotes');
  const [minUpvotes, setMinUpvotes] = useState<number>(0);

  // Time duration options (in hours)
  const timeOptions = [
    { value: '0.5', label: '30 minutes' },
    { value: '1', label: '1 hour' },
    { value: '2', label: '2 hours' },
    { value: '3', label: '3 hours' },
    { value: '6', label: '6 hours' },
    { value: '12', label: '12 hours' },
    { value: '18', label: '18 hours' },
    { value: '24', label: '1 day' },
    { value: '48', label: '2 days' },
    { value: '72', label: '3 days' },
    { value: '168', label: '1 week' },
    { value: '336', label: '2 weeks' },
    { value: '504', label: '3 weeks' },
    { value: '720', label: '1 month' },
    { value: '1440', label: '2 months' },
    { value: '2160', label: '3 months' },
    { value: '4320', label: '6 months' },
    { value: '8760', label: '1 year' },
  ];

  const handleNavigate = (page: string) => {
    switch (page) {
      case 'dashboard':
      case 'deep-analysis':
      case 'recommended':
      case 'faq-serp':
        navigate('/dashboard');
        break;
      case 'eco':
        navigate('/eco');
        break;
      case 'fact-check':
        navigate('/fact-check');
        break;
      case 'reddit-hn':
        break;
      default:
        console.log('Unknown navigation target:', page);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.subReddit.trim()) {
      toast.error('Please enter a Sub-Reddit');
      return;
    }

    if (!formData.timeUtc.trim()) {
      toast.error('Please select a time duration');
      return;
    }

    setIsLoading(true);
    setResult(null);

    try {
      // Calculate the UTC timestamp based on selected duration
      const now = new Date();
      const hoursBack = parseFloat(formData.timeUtc);
      const lastCheckTime = subHours(now, hoursBack);
      const lastCheckUtc = format(lastCheckTime, "yyyy-MM-dd'T'HH:mm:ss'Z'");

      const requestData = {
        subreddit: formData.subReddit.trim(),
        last_check_utc: lastCheckUtc
      };

      const response = await fetch('https://growtika.app.n8n.cloud/webhook/reddit-hn-workflow', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setResult(data);
      toast.success('Reddit-HN analysis completed successfully!');

    } catch (error) {
      console.error('Error submitting Reddit-HN request:', error);
      toast.error('Failed to analyze Reddit-HN coverage. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      subReddit: '',
      timeUtc: '12' // Reset to default 12 hours
    });
    setResult(null);
  };

  // Get seen and unseen from API response
  const seen = result?.seen || [];
  const unseen = result?.unseen || [];

  // Filter and sort articles function
  const filterAndSortArticles = (articles: any[]) => {
    // First filter by minimum upvotes
    const filtered = articles.filter(article => (article.ups || 0) >= minUpvotes);
    
    // Then sort
    const sorted = [...filtered];
    switch (sortBy) {
      case 'upvotes':
        return sorted.sort((a, b) => (b.ups || 0) - (a.ups || 0));
      case 'title':
        return sorted.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
      case 'ratio':
        return sorted.sort((a, b) => (b.upvote_ratio || 0) - (a.upvote_ratio || 0));
      default:
        return sorted;
    }
  };

  // Get filtered and sorted articles for current tab
  const currentArticles = filterAndSortArticles(activeTab === 'unpublished' ? unseen : seen);
  
  // Get filtered counts for display
  const filteredUnseenCount = filterAndSortArticles(unseen).length;
  const filteredSeenCount = filterAndSortArticles(seen).length;

  return (
    <div className="flex min-h-screen w-full">
      <AppSidebar 
        activePage="reddit-hn" 
        onNavigate={handleNavigate} 
      />
      
      <div className={cn(
        "flex-1 min-w-0 px-6 pb-12 overflow-x-hidden transition-all duration-300",
        !isMobile && (compact ? "ml-16" : "ml-64")
      )}>
        <div className="bg-gradient-to-br from-background via-background to-accent/5">
          <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="space-y-8">
          {/* Header */}
          <div className="text-center space-y-4">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl mb-4">
              <Hash className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
              Reddit–HN Coverage
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Analyze Reddit and Hacker News coverage for specific subreddits and time periods
            </p>
          </div>

          {/* Input Form */}
          <Card className="border-orange-200/20 shadow-lg">
            <CardHeader className="text-center pb-6">
            <CardTitle className="text-2xl text-orange-600">Coverage Analysis</CardTitle>
            <CardDescription>
              Enter subreddit and select time duration to analyze Reddit-HN coverage
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="subReddit" className="text-sm font-medium flex items-center gap-2">
                    <Hash className="h-4 w-4" />
                    Sub-Reddit
                  </Label>
                  <Input
                    id="subReddit"
                    placeholder="e.g., technology, programming, startups"
                    value={formData.subReddit}
                      onChange={(e) => handleInputChange('subReddit', e.target.value)}
                      className="transition-all duration-200 focus:ring-2 focus:ring-orange-500/20"
                      disabled={isLoading}
                    />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="timeUtc" className="text-sm font-medium flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Time Duration
                  </Label>
                  <Select 
                    value={formData.timeUtc} 
                    onValueChange={(value) => handleInputChange('timeUtc', value)}
                    disabled={isLoading}
                  >
                    <SelectTrigger className="transition-all duration-200 focus:ring-2 focus:ring-orange-500/20">
                      <SelectValue placeholder="Select time duration" />
                    </SelectTrigger>
                    <SelectContent>
                      {timeOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                </div>

                <div className="flex gap-4 pt-4">
                  <Button 
                    type="submit" 
                    disabled={isLoading || !formData.subReddit.trim() || !formData.timeUtc.trim()}
                    className="flex-1 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white font-medium py-3 transition-all duration-200 hover:shadow-lg hover:scale-[1.02]"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4" />
                        Analyze Coverage
                      </>
                    )}
                  </Button>

                  {(formData.subReddit || formData.timeUtc || result) && (
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={resetForm}
                      disabled={isLoading}
                      className="border-orange-200 text-orange-600 hover:bg-orange-50"
                    >
                      Reset
                    </Button>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Results */}
          {result && (
            <div className="space-y-6">
               {/* Summary */}
               <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                 <Card className="border-blue-200/20">
                   <CardContent className="pt-6">
                     <div className="text-center">
                       <div className="text-2xl font-bold text-blue-600">{filteredSeenCount + filteredUnseenCount}</div>
                       <div className="text-sm text-muted-foreground">
                         Total Articles{minUpvotes > 0 ? ` (≥${minUpvotes} upvotes)` : ''}
                       </div>
                     </div>
                   </CardContent>
                 </Card>
                 <Card className="border-green-200/20">
                   <CardContent className="pt-6">
                     <div className="text-center">
                       <div className="text-2xl font-bold text-green-600">{filteredUnseenCount}</div>
                       <div className="text-sm text-muted-foreground">
                         Unpublished on HackerNews{minUpvotes > 0 ? ` (≥${minUpvotes} upvotes)` : ''}
                       </div>
                     </div>
                   </CardContent>
                 </Card>
                 <Card className="border-purple-200/20">
                   <CardContent className="pt-6">
                     <div className="text-center">
                       <div className="text-2xl font-bold text-purple-600">{filteredSeenCount}</div>
                       <div className="text-sm text-muted-foreground">
                         Published on HackerNews{minUpvotes > 0 ? ` (≥${minUpvotes} upvotes)` : ''}
                       </div>
                     </div>
                   </CardContent>
                 </Card>
               </div>

              {/* Tab Navigation & Sorting Controls */}
              <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
                <div className="flex gap-2">
                   <Button
                     variant={activeTab === 'unpublished' ? 'default' : 'outline'}
                     onClick={() => setActiveTab('unpublished')}
                     className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white border-green-600"
                   >
                     <EyeOff className="h-4 w-4" />
                     Unpublished ({filteredUnseenCount})
                   </Button>
                   <Button
                     variant={activeTab === 'published' ? 'default' : 'outline'}
                     onClick={() => setActiveTab('published')}
                     className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white border-purple-600"
                   >
                     <TrendingUp className="h-4 w-4" />
                     Published ({filteredSeenCount})
                   </Button>
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                    <Label htmlFor="minUpvotes" className="text-sm font-medium whitespace-nowrap">
                      Min Upvotes:
                    </Label>
                    <Input
                      id="minUpvotes"
                      type="number"
                      min="0"
                      value={minUpvotes}
                      onChange={(e) => setMinUpvotes(Number(e.target.value) || 0)}
                      className="w-20"
                      placeholder="0"
                    />
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
                    <Select value={sortBy} onValueChange={(value: any) => setSortBy(value)}>
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="upvotes">Sort by Upvotes</SelectItem>
                        <SelectItem value="title">Sort by Title</SelectItem>
                        <SelectItem value="ratio">Sort by Ratio</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Articles Display */}
              {currentArticles.length > 0 && (
                <Card className={cn(
                  "shadow-lg",
                  activeTab === 'unpublished' ? "border-green-200/20" : "border-purple-200/20"
                )}>
                  <CardHeader>
                    <CardTitle className={cn(
                      "text-xl flex items-center gap-2",
                      activeTab === 'unpublished' ? "text-green-600" : "text-purple-600"
                    )}>
                      {activeTab === 'unpublished' ? <EyeOff className="h-5 w-5" /> : <TrendingUp className="h-5 w-5" />}
                      {activeTab === 'unpublished' ? 'Unpublished on HackerNews' : 'Published on HackerNews'} ({currentArticles.length})
                    </CardTitle>
                    <CardDescription>
                      {activeTab === 'unpublished' 
                        ? `Articles from r/${formData.subReddit} that are not published on HackerNews.`
                        : `Articles from r/${formData.subReddit} that are published on HackerNews.`
                      }
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4">
                      {currentArticles.map((item: any, index: number) => (
                        <div 
                          key={index} 
                          className={cn(
                            "border rounded-lg p-4 hover:border-opacity-75 transition-colors",
                            activeTab === 'unpublished' 
                              ? "border-green-200/30 bg-green-50/30 hover:border-green-300" 
                              : "border-purple-200/30 bg-purple-50/30 hover:border-purple-300"
                          )}
                        >
                          <div className="space-y-3">
                            <div className="flex items-start justify-between gap-4">
                               <div className="flex-1 min-w-0">
                                 <h3 className="font-medium text-foreground line-clamp-2 mb-2">
                                   {item.title || (item.url ? new URL(item.url).hostname : 'Unknown Source')}
                                 </h3>
                                 <p className="text-sm text-muted-foreground mb-3">
                                   {item.url ? new URL(item.url).hostname : 'unknown source'}
                                 </p>
                                 <div className="flex gap-4 text-xs text-muted-foreground">
                                   {item.ups && <span className="flex items-center gap-1">↑ {item.ups} upvotes</span>}
                                   {item.upvote_ratio && <span className="flex items-center gap-1">📊 {Math.round(item.upvote_ratio * 100)}% upvoted</span>}
                                 </div>
                               </div>
                              <div className="text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded">
                                r/{item.subreddit}
                              </div>
                            </div>
                            
                            <div className="flex flex-wrap gap-3">
                               <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => window.open(item.url, '_blank')}
                                className="text-blue-600 border-blue-200 hover:bg-blue-50"
                              >
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                                View Article
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => window.open(item.preview, '_blank')}
                                className="text-orange-600 border-orange-200 hover:bg-orange-50"
                              >
                                <Hash className="w-4 h-4 mr-2" />
                                View on Reddit
                              </Button>
                              {activeTab === 'published' && item.hn_preview_link && (
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  onClick={() => window.open(item.hn_preview_link, '_blank')}
                                  className="text-purple-600 border-purple-200 hover:bg-purple-50"
                                >
                                  <TrendingUp className="w-4 h-4 mr-2" />
                                  View on HackerNews
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Empty State */}
              {(seen.length + unseen.length) === 0 && (
                <Card className="border-gray-200/20">
                  <CardContent className="pt-6">
                    <div className="text-center py-8 text-muted-foreground">
                      <Hash className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No articles found for the specified time period and filters.</p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const RedditHN: React.FC = () => {
  return (
    <div className="min-h-screen">
      <Header />
      
      <main className="pt-20">
        <SidebarProvider>
          <RedditHNLayout />
        </SidebarProvider>
      </main>
      
      <DashboardFooter />
    </div>
  );
};

export default RedditHN;